import { Search, XCircle } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  popularProductWeightEstimates,
  searchProductWeightEstimates,
  type ProductWeightEstimate,
} from "../helpers/product-weight-estimates";

type ProductWeightSearchProps = {
  value: string;
  inputClassName: string;
  onChange: (value: string) => void;
  onSelect: (product: ProductWeightEstimate) => void;
  onClearSelection: () => void;
};

export function ProductWeightSearch({
  value,
  inputClassName,
  onChange,
  onSelect,
  onClearSelection,
}: ProductWeightSearchProps) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchTerm = value.trim();
  const searchResults = useMemo(() => searchProductWeightEstimates(searchTerm), [searchTerm]);
  const productsToShow = searchTerm ? searchResults : popularProductWeightEstimates;
  const hasSearchResults = productsToShow.length > 0;

  useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  const selectProduct = (product: ProductWeightEstimate) => {
    onSelect(product);
    setIsOpen(false);
  };

  const clearSearch = () => {
    onChange("");
    onClearSelection();
    setIsOpen(true);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <label className="relative block">
        <span className="sr-only">Search product type</span>
        <Search
          className="pointer-events-none absolute left-6 top-1/2 h-6 w-6 -translate-y-1/2 text-[#9a9a9a]"
          aria-hidden="true"
        />
        <input
          name="productType"
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
            onClearSelection();
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search product type"
          autoComplete="off"
          className={`${inputClassName} pl-[62px] pr-14`}
        />
        {value && (
          <button
            type="button"
            onClick={clearSearch}
            className="absolute right-5 top-1/2 -translate-y-1/2 text-[#9a9a9a] transition hover:text-[#0f4694]"
            aria-label="Clear product search"
          >
            <XCircle className="h-5 w-5" aria-hidden="true" />
          </button>
        )}
      </label>

      {isOpen && (
        <div className="absolute left-0 right-0 top-[calc(100%+16px)] z-20 overflow-hidden rounded-2xl border border-[#e6e6e6] bg-white shadow-[0_18px_45px_rgba(15,70,148,0.10)]">
          {hasSearchResults ? (
            <>
              <p className="px-5 pb-3 pt-5 text-sm font-medium text-[#595959] sm:px-8 sm:pt-7">
                {searchTerm ? "Matching Products" : "Popular Searches"}
              </p>
              <div className="max-h-[300px] overflow-y-auto sm:max-h-[360px]">
                {productsToShow.map((product) => (
                  <button
                    key={product.name}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => selectProduct(product)}
                    className="block w-full border-t border-[#eeeeee] px-5 py-4 text-left text-sm font-medium text-black transition hover:bg-[#f3f7fc] focus:bg-[#f3f7fc] focus:outline-none sm:px-8 sm:py-5 sm:text-base"
                  >
                    {product.name}
                  </button>
                ))}
              </div>
              {searchTerm && (
                <p className="border-t border-[#eeeeee] px-5 py-4 text-xs text-[#8a8a8a] sm:px-8 sm:text-sm">
                  If the item you want is not on the list, try a different search
                </p>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center px-6 py-8 text-center sm:px-10 sm:py-12">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#eef4fd] sm:h-20 sm:w-20">
                <Search className="h-8 w-8 text-[#0f4694] sm:h-11 sm:w-11" aria-hidden="true" />
              </span>
              <p className="mt-5 text-sm font-medium text-[#323232] sm:text-base">
                No matching products found
              </p>
              <div className="mt-4 text-left text-xs leading-relaxed text-[#9a9a9a] sm:text-sm">
                <p>Try:</p>
                <ul className="list-disc pl-5">
                  <li>Checking Spelling</li>
                  <li>Using a broader term</li>
                  <li>Choosing a similar term</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
